
import React, { useState, useMemo } from 'react';
import type { ScheduleEntry, Shift, AssignmentStatus, DayOfWeek, AvailabilityStatus } from '../../types';
import { ALL_ASSIGNMENT_STATUSES } from '../../types';
import Popover from '../ui/Popover';
import { Icons } from '../ui/Icons';
import { toast } from 'react-hot-toast';
import { getDayOfWeekFromDate } from '../../utils/dateUtils';
import ConfirmationModal from '../ui/ConfirmationModal';


interface ScheduleCellProps {
    staffId: string;
    date: string;
    entry?: ScheduleEntry;
    shifts: Shift[];
    onUpdate: (staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean) => void;
    draggedShiftId: string | null;
    personName: string;
    personAvailability?: {
        availability: { [key in DayOfWeek]: AvailabilityStatus };
        availabilityOverrides?: { [date: string]: AvailabilityStatus };
    };
}

const statusConfig: { [key in AssignmentStatus]: { label: string, icon: React.ReactNode, color: string } } = {
    ASSIGNED: { label: 'Assigned', icon: <Icons.Clock className="w-4 h-4" />, color: 'bg-gray-200 text-gray-800 dark:bg-slate-600 dark:text-slate-100' },
    OFF: { label: 'Off', icon: <Icons.Coffee className="w-4 h-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300' },
    SICK: { label: 'Sick', icon: <Icons.Thermometer className="w-4 h-4" />, color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/25 dark:text-yellow-300' },
    VACATION: { label: 'Vacation', icon: <Icons.Plane className="w-4 h-4" />, color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300' },
    ON_CALL: { label: 'On Call', icon: <Icons.Phone className="w-4 h-4" />, color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/25 dark:text-purple-300' },
    UNASSIGNED: { label: 'Unassigned', icon: <Icons.HelpCircle className="w-4 h-4" />, color: 'bg-gray-100 text-gray-500 dark:bg-slate-700/50 dark:text-slate-400' },
};

const formatTimeToAMPM = (time: string): string => {
    if (!time) return '';
    const [hoursStr, minutesStr] = time.split(':');
    let hours = parseInt(hoursStr, 10);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours}${ampm}`;
};


const ScheduleCell: React.FC<ScheduleCellProps> = ({ staffId, date, entry, shifts, onUpdate, draggedShiftId, personName, personAvailability }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [forceAssignData, setForceAssignData] = useState<{ shiftId: string | null, status: AssignmentStatus } | null>(null);
    const shift = entry?.shiftId ? shifts.find(s => s.id === entry.shiftId) : null;

    const { isSpecificUnavailability, isDefaultUnavailability } = useMemo(() => {
        if (!personAvailability) {
            return { isSpecificUnavailability: false, isDefaultUnavailability: false };
        }
        
        // Safe date parsing to local time to avoid timezone issues with YYYY-MM-DD strings
        const [year, month, day] = date.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        const dayOfWeek = getDayOfWeekFromDate(localDate);

        const override = personAvailability.availabilityOverrides?.[date];
        const resolvedStatus = override || (personAvailability.availability && personAvailability.availability[dayOfWeek]) || 'Available';
        
        return {
            isSpecificUnavailability: resolvedStatus === 'Unavailable' && !!override,
            isDefaultUnavailability: resolvedStatus === 'Unavailable' && !override,
        };
    }, [date, personAvailability]);

    const handleAssignmentAttempt = (shiftId: string | null, status: AssignmentStatus) => {
        // Allow setting non-assignment statuses (like SICK, VACATION) regardless of availability
        if (status !== 'ASSIGNED') {
            onUpdate(staffId, date, shiftId, status, false);
            return;
        }

        // Hard lock for specific unavailability
        if (isSpecificUnavailability) {
            toast.error('This staff member is not available on this specific date.');
            return;
        }

        // Soft lock for default unavailability
        if (isDefaultUnavailability) {
            setForceAssignData({ shiftId, status });
            return;
        }
        
        onUpdate(staffId, date, shiftId, status, false);
    };

    const handleConfirmForceAssign = () => {
        if (forceAssignData) {
            onUpdate(staffId, date, forceAssignData.shiftId, forceAssignData.status, true);
        }
        setForceAssignData(null);
    };

    const handleCancelForceAssign = () => {
        setForceAssignData(null);
    };

    const handleDrop = (e: React.DragEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        if (isSpecificUnavailability) return;
        
        const droppedShiftId = e.dataTransfer.getData('shiftId');
        if (droppedShiftId) {
            handleAssignmentAttempt(droppedShiftId, 'ASSIGNED');
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLTableCellElement>) => {
        e.preventDefault();
        if (!isSpecificUnavailability) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };
    
    const PopoverContent = ({ close }: { close: () => void }) => (
        <div className="w-56">
            <div className="p-2 font-semibold text-gray-700 dark:text-gray-200 border-b dark:border-slate-700">Assign Shift</div>
            <div className="max-h-40 overflow-y-auto">
                {shifts.map(s => (
                    <button key={s.id} onClick={() => { handleAssignmentAttempt(s.id, 'ASSIGNED'); close(); }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }}></span>
                        <span>{s.name}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">{s.startTime}-{s.endTime}</span>
                    </button>
                ))}
            </div>
            <div className="p-2 font-semibold text-gray-700 dark:text-gray-200 border-t dark:border-slate-700">Set Status</div>
             {ALL_ASSIGNMENT_STATUSES.filter(s => s !== 'ASSIGNED').map(status => (
                <button key={status} onClick={() => { handleAssignmentAttempt(null, status); close(); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 flex items-center gap-2">
                    {statusConfig[status].icon} {statusConfig[status].label}
                </button>
             ))}
        </div>
    );

    const renderCellContent = () => {
        const originalContent = (() => {
            if (shift) {
                const formattedStartTime = formatTimeToAMPM(shift.startTime);
                const formattedEndTime = formatTimeToAMPM(shift.endTime);
                return (
                    <div 
                        className="p-1.5 rounded-md text-white font-semibold text-center h-full w-full flex flex-col justify-center" 
                        style={{ backgroundColor: shift.color }}
                    >
                        <span className="text-sm leading-tight">{`${formattedStartTime}-${formattedEndTime}`}</span>
                        <span className="text-xs opacity-90 leading-tight">{`${shift.startTime} - ${shift.endTime}`}</span>
                    </div>
                );
            }
            if (entry && entry.status !== 'UNASSIGNED') {
                const config = statusConfig[entry.status];
                return (
                    <div className={`w-full h-full rounded-md text-sm flex items-center justify-center gap-1.5 font-medium ${config.color}`}>
                         {config.icon}
                        <span>{config.label}</span>
                    </div>
                );
            }
            return (
                <div className="w-full h-full rounded-md bg-gray-50 dark:bg-slate-800/50"></div>
            );
        })();

        return (
            <div className="relative h-full w-full">
                <div className={isSpecificUnavailability ? 'opacity-40' : ''}>
                    {originalContent}
                </div>
                {isSpecificUnavailability && (
                    <div className="absolute inset-0 flex items-center justify-center" title="Specifically Unavailable">
                        <Icons.XCircle className="w-6 h-6 text-red-600 dark:text-red-500" />
                    </div>
                )}
                {entry?.isForced && (
                    <div className="absolute bottom-1 right-1" title="This shift was force assigned against default availability.">
                        <Icons.AlertTriangle className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400 bg-white dark:bg-slate-700 rounded-full p-0.5 shadow" />
                    </div>
                )}
            </div>
        );
    };

    const cellWrapper = (
        <div className={`h-full w-full rounded-md transition-all duration-150 ${!isSpecificUnavailability ? 'cursor-pointer hover:ring-2 hover:ring-indigo-500 hover:ring-offset-1 dark:hover:ring-offset-slate-800' : 'cursor-not-allowed'}`}>
            {renderCellContent()}
        </div>
    );

    return (
        <td
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`align-middle h-16 border-l border-gray-200 dark:border-slate-700 transition-colors duration-150 ${isDragOver && !isSpecificUnavailability ? 'bg-indigo-100 dark:bg-indigo-900/50' : ''}`}
        >
            <div className="p-1 h-full w-full">
                {isSpecificUnavailability ? (
                    <div title={`${personName} has marked this date as specifically unavailable.`}>{cellWrapper}</div>
                ) : (
                    <Popover wrapperClassName="h-full w-full" content={<PopoverContent close={() => {}} />}>
                        {cellWrapper}
                    </Popover>
                )}
            </div>

            {forceAssignData && (
                <ConfirmationModal
                    title="Force Assignment"
                    message={`${personName} is not normally available on this day. Do you want to force assign this shift?`}
                    confirmButtonText="Force Assign"
                    onConfirm={handleConfirmForceAssign}
                    onCancel={handleCancelForceAssign}
                />
            )}
        </td>
    );
};

export default ScheduleCell;